import type { JSX } from 'react';
import { App, Skeleton, Switch, Tooltip } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FireOutlined, TrophyOutlined } from '@ant-design/icons';

import { educationApi } from '../lib/wikiApi';

/**
 * «Рейтинг команды» на главной (фаза 3.6, вид HUB 2.0): мои очки и серия,
 * бейджи, opt-in рейтинг по точке (сотрудники) / по сети (CRM-пользователи).
 * Полный экран очков и бейджей — /points (W3).
 */
export function PointsWidget(): JSX.Element | null {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const q = useQuery({ queryKey: ['wiki', 'gamification', 'mine'], queryFn: () => educationApi.gamificationMine(), staleTime: 60_000 });
  const optMut = useMutation({
    mutationFn: (on: boolean) => educationApi.gamificationPrefs(on),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wiki', 'gamification', 'mine'] }),
    onError: (e: Error) => message.error(e.message),
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 3 }} />;
  const d = q.data;
  if (!d) return null;
  const max = Math.max(1, ...d.leaderboard.map((r) => r.points));
  const earned = d.badges.filter((b) => b.earned);
  return (
    <section className="fk-hub-card">
      <div className="fk-hub-card-head">
        <div className="fk-hub-card-title">
          <TrophyOutlined />
          <h2>{d.leaderboardOptIn ? 'Рейтинг команды' : 'Мои очки'}</h2>
        </div>
        <Link to="/points" className="fk-hub-card-link">
          Очки и бейджи →
        </Link>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1, color: 'var(--fk-text)' }}>{d.points.toLocaleString('ru-RU')}</div>
          <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>очков</div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--fk-text-2)' }}>
          <FireOutlined style={{ color: d.streakDays >= 3 ? 'var(--fk-orange)' : 'var(--fk-text-muted)' }} /> серия {d.streakDays} {plural(d.streakDays, ['день', 'дня', 'дней'])}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(earned.length ? earned : d.badges).slice(0, 5).map((b) => (
            <Tooltip key={b.code} title={`${b.name}: ${b.description}${b.at ? ` · получен ${new Date(b.at).toLocaleDateString('ru-RU')}` : ''}`}>
              <span style={{ fontSize: 18, filter: b.earned ? 'none' : 'grayscale(1)', opacity: b.earned ? 1 : 0.35, cursor: 'default' }}>{b.icon}</span>
            </Tooltip>
          ))}
        </div>
      </div>

      {d.leaderboardOptIn && d.leaderboard.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {d.leaderboard.slice(0, 5).map((r, i) => (
            <div key={r.rank} className="fk-hub-rank" data-first={i === 0 ? 'true' : 'false'} data-me={r.isMe ? 'true' : 'false'}>
              <span>{r.rank}</span>
              <div style={{ minWidth: 0 }}>
                <div className="fk-hub-ellipsis" style={{ fontWeight: r.isMe ? 800 : 600 }}>{r.name}</div>
                <div className="fk-hub-bar">
                  <div style={{ width: `${Math.round((r.points / max) * 100)}%` }} />
                </div>
              </div>
              <span style={{ fontWeight: 700, fontFamily: 'var(--fk-font-mono)', fontSize: 12 }}>{r.points.toLocaleString('ru-RU')}</span>
            </div>
          ))}
          {d.leaderboard.length <= 1 && <div style={{ fontSize: 12, color: 'var(--fk-text-muted)' }}>Пока вы один в рейтинге — пригласите коллег включить участие.</div>}
        </div>
      )}
      {d.recent.length > 0 && !d.leaderboardOptIn && (
        <div style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginBottom: 8 }}>
          {d.recent.slice(0, 3).map((r, i) => (
            <div key={i}>
              +{r.delta} · {r.reason}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, borderTop: '1px solid var(--fk-row-line)', paddingTop: 10, color: 'var(--fk-text-2)' }}>
        <Switch size="small" checked={d.leaderboardOptIn} loading={optMut.isPending} onChange={(v) => optMut.mutate(v)} />
        <span>Участвовать в рейтинге{d.scope ? ` (${d.scope})` : ''}</span>
      </div>
    </section>
  );
}

function plural(n: number, f: [string, string, string]): string {
  const m10 = n % 10,
    m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return f[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return f[1];
  return f[2];
}
